import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  MenuItem,
  Select,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import SearchIcon from "@mui/icons-material/Search";
// import LaunchIcon from "@mui/icons-material/Launch";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { EditPracticeDialog } from "../../components/practice/EditPracticeDialog";
import { AddPracticeDialog } from "../../components/practice/AddPracticeDialog";

const PracticePage = () => {
  const { user, token } = useAuthContext();

  const { darkMode } = useContext(ThemeContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [filteredPractices, setFilteredPractices] = useState([]); // State for filtered practices
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [practices, setPractices] = useState([]); // State to store users fetched from backend
  const [page, setPage] = useState(0); // Pagination page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Users per page
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const { enqueueSnackbar } = useSnackbar();

  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [practiceToEdit, setPracticeToEdit] = useState(null); // New state for the user to be deleted

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "contactname", // Default sort key
    direction: "asc", // Default sort direction
  });

  useEffect(() => {
    // Fetch users from the backend
    if (!user || !token) return;
    const fetchPractices = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/practice/withstatus`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setPractices(response.data);
        setFilteredPractices(response.data); // Initialize filtered practices
      } catch (err) {
        console.error("Error fetching practices:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPractices();
  }, [token, user]);

  useEffect(() => {
    // Filter practices based on the search query
    setFilteredPractices(
      practices.filter((practice) => {
        const statusText =
          practice.status === 0
            ? "Active"
            : practice.status === 1
            ? "Suspended"
            : "Pending";

        return (
          (practice.contactname || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (practice.practicename || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (practice.email || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          statusText.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
    );
  }, [searchQuery, practices]);

  const handleStatusChange = async (event, practiceid) => {
    setLoading(true);
    const newStatus = event.target.value;
    let suspendedStatus;
    let subscribedStatus;

    switch (newStatus) {
      case "Active":
        suspendedStatus = 0;
        subscribedStatus = 1;
        break;
      case "Suspended":
        suspendedStatus = 1;
        subscribedStatus = 0;
        break;
      case "Pending":
        suspendedStatus = 2;
        subscribedStatus = 0;
        break;
      case "Trial":
        suspendedStatus = 0;
        subscribedStatus = 0;
        break;
      default:
        suspendedStatus = 0; // Default to Active if unknown status
        subscribedStatus = 1; // Default to Active if unknown status
    }

    await axios.put(
      `${process.env.REACT_APP_API_URL}/users/changestatus/${practiceid}`,
      { suspended: suspendedStatus, subscribed: subscribedStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setPractices((prevPractices) =>
      prevPractices.map((practice) =>
        practice.practiceid === practiceid
          ? {
              ...practice,
              status: suspendedStatus,
              subscribed: subscribedStatus, // Update the subscribed status as well
            }
          : practice
      )
    );
    setLoading(false);
    enqueueSnackbar("Successfully Changed!", { variant: "success" });
  };

  const userStatusResult = (subscribed, status) => {
    if (subscribed === 1 && status === 0) return "Active";
    if (subscribed === 1 && status === 1) return "Suspended";
    if (subscribed === 1 && status === 2) return "N/A";
    if (subscribed === 0 && status === 0) return "Trial";
    if (subscribed === 0 && status === 1) return "Suspended";
    if (subscribed === 0 && status === 2) return "Pending";
    return "Unknown"; // Default case for unexpected values
  };

  const handleEdit = async (practiceid) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/practice/${practiceid}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setPracticeToEdit(response.data);
    setOpenEditDialog(true);
  };

  // const handleGoToPractice = async (practiceid) => {
  //   alert("Go to Practice");
  // };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleEditPractice = async (data) => {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/practice/${practiceToEdit.practiceid}`,
      {
        ...data,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setPractices((prevPractices) =>
      prevPractices.map((practice) =>
        practice.practiceid === practiceToEdit.practiceid
          ? {
              ...practice,
              contactname: data.contactname,
              practicename: data.practicename,
              features: data.features,
              customerid: data.customerid,
              address1: data.practice,
              address2: data.practice2,
              city: data.city,
              state: data.state,
              country: data.country,
              postalcode: data.postalcode,
              contactnumber: data.contactnumber,
              email: data.email,
              blurb: data.blurb,
            }
          : practice
      )
    );
    setOpenEditDialog(false);
    enqueueSnackbar("Successfully Changed!", { variant: "success" });
  };

  const handleAddPracticeClicked = () => {
    setOpenDialog(true);
  };

  const handleAddPractice = async (newPractice) => {
    // Send a POST request to the backend API to add the new user
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/practice`,
      newPractice,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const newUserData = {
      username: newPractice.username,
      password: newPractice.password,
      securitytype: 2,
      patientid: 0,
      practiceid: response.data.practiceid,
      subscribed: 1,
      suspended: 0,
      description: newPractice.username,
      email: "",
    };

    await axios.post(`${process.env.REACT_APP_API_URL}/users`, newUserData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setPractices((prevPractices) => [
      ...prevPractices,
      {
        ...response.data,
      },
    ]);
    enqueueSnackbar("Successfully Added!", { variant: "success" });
    setOpenDialog(false);
  };

  const handleSort = (column) => {
    const isAscending =
      sortConfig.key === column && sortConfig.direction === "asc";
    const newDirection = isAscending ? "desc" : "asc";

    setSortConfig({
      key: column,
      direction: newDirection,
    });

    // Sort users based on the column and direction
    const sortedPractices = [...practices].sort((a, b) => {
      if (a[column] < b[column]) return newDirection === "asc" ? -1 : 1;
      if (a[column] > b[column]) return newDirection === "asc" ? 1 : -1;
      return 0;
    });

    setPractices(sortedPractices);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedPractices = filteredPractices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        pt: "60px",
        pl: 5,
        pr: 10,
      }}
    >
      {/* Loading State */}
      <Backdrop sx={{ zIndex: 1400 }} open={loading}>
        <CircularProgress size={80} />
      </Backdrop>

      {!loading && (
        <>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "28px",
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              fontWeight: 600,
              textAlign: "left",
              mb: 5,
            }}
          >
            Global Practice Management
          </Typography>
          <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
            <TextField
              variant="filled"
              placeholder="Search practices"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ mt: -2, ml: 1 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<Add />}
              variant="contained"
              sx={{
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 3,
                py: 2,
                background: darkMode ? "#2D2E30" : "#E6E6E6",
                color: darkMode
                  ? getColors.primaryTextDark
                  : getColors.secondaryTextLight,
                "&:hover": {
                  color: getColors.primaryTextDark,
                },
              }}
              onClick={handleAddPracticeClicked}
            >
              Add Practice
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ mb: 1 }}>
            <Table>
              <TableHead
                sx={{
                  background: darkMode ? "#1C1D21" : "#E6E6E6",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: darkMode
                    ? getColors.appBarBorderDark
                    : getColors.thumbnailBorderLight,
                }}
              >
                <TableRow sx={{ height: "60px" }}>
                  {["contactname", "practicename", "email", "status"].map(
                    (header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          borderColor: darkMode
                            ? getColors.appBarBorderDark
                            : getColors.thumbnailBorderLight,
                          px: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => handleSort(header)}
                      >
                        <Box
                          display="flex"
                          sx={{
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                            ...(header === "contactname" ||
                            header === "practicename" ||
                            header === "email" ||
                            header === "subscribed"
                              ? {
                                  px: "20px",
                                }
                              : { justifyContent: "center" }),
                          }}
                        >
                          {header.toUpperCase()}
                          {sortConfig.key === header && (
                            <>
                              {sortConfig.direction === "asc" ? (
                                <ArrowDownwardIcon sx={{ ml: 1 }} />
                              ) : (
                                <ArrowUpwardIcon sx={{ ml: 1 }} />
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    )
                  )}
                  {/* <TableCell
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "20px",
                      color: darkMode
                        ? getColors.listItemTextDark
                        : getColors.thumbnailTextLight,
                      textAlign: "center",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    ACTIONS
                  </TableCell> */}
                  <TableCell
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "20px",
                      color: darkMode
                        ? getColors.listItemTextDark
                        : getColors.thumbnailTextLight,
                      textAlign: "center",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  ></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedPractices.map((practice) => (
                  <TableRow
                    key={practice.practiceid}
                    sx={{
                      height: "60px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {practice.contactname}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {practice.practicename}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {practice.email}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        padding: 0,
                      }}
                    >
                      {userStatusResult(
                        practice.subscribed,
                        practice.status
                      ) === "Active" ||
                      userStatusResult(practice.subscribed, practice.status) ===
                        "Suspended" ||
                      userStatusResult(practice.subscribed, practice.status) ===
                        "Trial" ||
                      userStatusResult(practice.subscribed, practice.status) ===
                        "Pending" ? (
                        <FormControl>
                          <Select
                            value={userStatusResult(
                              practice.subscribed,
                              practice.status
                            )}
                            onChange={(e) =>
                              handleStatusChange(e, practice.practiceid)
                            }
                            sx={{
                              width: "150px",
                              fontFamily: "Inter",
                              fontWeight: 500,
                              fontSize: "18px",
                              color: (() => {
                                const status = userStatusResult(
                                  practice.subscribed,
                                  practice.status
                                );
                                return status === "Active"
                                  ? "#00AF4E"
                                  : status === "Suspended"
                                  ? "#FF0000"
                                  : status === "Trial"
                                  ? "#0000FF"
                                  : status === "Pending"
                                  ? "#FFA500"
                                  : "#808080"; // Default color for unknown status
                              })(),
                            }}
                          >
                            <MenuItem value={"Active"}>Active</MenuItem>
                            <MenuItem value={"Suspended"}>Suspended</MenuItem>
                            <MenuItem value={"Pending"}>Pending</MenuItem>
                            <MenuItem value={"Trial"}>Trial</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "18px",
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                          }}
                        >
                          N/A
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleEdit(practice.practiceid)}
                      >
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          width="max-content"
                          sx={{
                            borderRadius: "50%",
                            padding: "8px",
                            color: "#007FFF",
                            bgcolor: "#E9F3FF",
                            alignItems: "center",
                            "&:hover": {
                              color: "blue",
                            },
                          }}
                        >
                          <BorderColorOutlinedIcon />
                        </Box>
                        {/* <Typography
                          sx={{
                            ml: "13px",
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "18px",
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                          }}
                        >
                          Edit
                        </Typography> */}
                      </Box>
                    </TableCell>
                    {/* <TableCell
                      sx={{
                        textAlign: "center",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleGoToPractice(practice.practiceid)}
                      >
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          width="max-content"
                          sx={{
                            borderRadius: "50%",
                            padding: "8px",
                            color: "#007FFF",
                            bgcolor: "#E9F3FF",
                            alignItems: "center",
                            "&:hover": {
                              color: "blue",
                            },
                          }}
                        >
                          <LaunchIcon />
                        </Box>
                      </Box>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={filteredPractices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <EditPracticeDialog
            open={openEditDialog}
            onClose={handleCancelEdit}
            practiceData={practiceToEdit}
            onEdit={handleEditPractice}
          />
          {/* Dialog for adding User/Admin */}
          <AddPracticeDialog
            open={openDialog}
            setOpen={setOpenDialog}
            onAddPractice={handleAddPractice}
          />
        </>
      )}
    </Box>
  );
};

export default PracticePage;
